(function () {
  if (customElements.get('minaco-product')) return;

  class MinacoProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.gallery = this.querySelector('[data-minaco-product-gallery]');
      this.progressThumb = this.querySelector('[data-minaco-carousel-progress-thumb]');
      this.variantSelect = this.querySelector('[data-minaco-variant-select]');
      this.price = this.querySelector('[data-minaco-product-price]');
      this.addButton = this.querySelector('[data-minaco-add-button]');
      this.addLabel = this.querySelector('[data-minaco-add-label]');
      this.lightbox = this.querySelector('[data-minaco-lightbox]');
      this.lightboxSlides = Array.from(this.querySelectorAll('[data-minaco-lightbox-slide]'));
      this.lightboxPrevious = this.querySelector('[data-minaco-lightbox-previous]');
      this.lightboxNext = this.querySelector('[data-minaco-lightbox-next]');
      this.progress = this.querySelector('[data-minaco-carousel-progress]');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.mobileCartSheet = this.querySelector('[data-minaco-mobile-cart-sheet]');
      this.mobileCartContinue = this.querySelector('[data-minaco-mobile-cart-continue]');
      this.mobileCartCheckout = this.querySelector('[data-minaco-mobile-cart-checkout]');
      this.mobileCartView = this.querySelector('[data-minaco-mobile-cart-view]');
      this.currentLightboxIndex = 0;
      this.carouselTimer = 0;
      this.carouselReady = false;
      this.suppressOpen = false;

      try {
        this.variants = JSON.parse(this.querySelector('[data-minaco-variants]')?.textContent || '[]');
      } catch (error) {
        this.variants = [];
      }

      this.addEventListener('change', this.handleChange.bind(this), { signal: this.signal });
      this.addEventListener('click', this.handleClick.bind(this), { signal: this.signal });
      this.addEventListener('keydown', this.handleKeydown.bind(this), { signal: this.signal });
      this.addEventListener('submit', this.handleSubmit.bind(this), { signal: this.signal });
      this.gallery?.addEventListener('scroll', this.handleCarouselScroll.bind(this), {
        passive: true,
        signal: this.signal
      });
      this.gallery?.addEventListener('pointerdown', this.handlePointerDown.bind(this), {
        passive: true,
        signal: this.signal
      });
      this.gallery?.addEventListener('pointerup', this.handlePointerUp.bind(this), {
        passive: true,
        signal: this.signal
      });
      window.addEventListener('resize', this.handleResize.bind(this), { passive: true, signal: this.signal });

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.handleBreakpointChange.bind(this), { signal: this.signal });
      }

      this.mobileCartContinue?.addEventListener('click', () => this.closeMobileCartSheet(), { signal: this.signal });
      this.mobileCartView?.addEventListener('click', () => this.openCartFromMobileSheet(), { signal: this.signal });
      this.mobileCartSheet?.addEventListener('click', (event) => {
        if (event.target === this.mobileCartSheet) this.closeMobileCartSheet();
      }, { signal: this.signal });
      this.mobileCartSheet?.addEventListener('close', () => {
        document.documentElement.classList.remove('minaco-product-mobile-cart-open');
      }, { signal: this.signal });

      this.syncVariant(false);
      this.syncHeaderOffset();
      this.syncAccordions();
      this.syncCarousel();
    }

    disconnectedCallback() {
      window.clearTimeout(this.carouselTimer);
      this.abortController?.abort();
      this.initialized = false;
    }

    handleChange(event) {
      if (!event.target.matches('[data-option-value]')) return;
      this.syncVariant(true);
    }

    async handleSubmit(event) {
      const form = event.target.closest('[data-minaco-product-form]');
      if (!form || !this.contains(form)) return;

      // Keep the native Shopify product form as the no-JS fallback, but when
      // JavaScript is available add through Ajax so the customer stays on the
      // product page and the existing MINA&CO cart drawer opens immediately.
      event.preventDefault();
      if (this.addButton?.disabled || form.dataset.minacoSubmitting === 'true') return;

      form.dataset.minacoSubmitting = 'true';
      this.addButton?.setAttribute('aria-busy', 'true');
      if (this.addButton) this.addButton.disabled = true;
      this.clearAjaxFormMessage(form);

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(form)
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.description || payload.message || 'Unable to add this item to your cart.');
        }

        const drawer = window.MinacoCartDrawerController;
        if (drawer?.refresh) await drawer.refresh();

        if (this.mobileQuery.matches) {
          this.openMobileCartSheet();
        } else if (drawer?.open) {
          drawer.open(this.addButton);
        } else {
          // Desktop keeps the existing cart-drawer behaviour unchanged.
          document.dispatchEvent(new CustomEvent('minaco:cart:open', { detail: { trigger: this.addButton } }));
        }
      } catch (error) {
        console.error('[MINA&CO product add]', error);
        this.showAjaxFormMessage(form, error?.message || 'Unable to add this item to your cart.');
      } finally {
        delete form.dataset.minacoSubmitting;
        this.addButton?.removeAttribute('aria-busy');
        this.syncVariant(false);
      }
    }

    openMobileCartSheet() {
      if (!this.mobileCartSheet || !this.mobileQuery.matches) return;
      if (typeof this.mobileCartSheet.showModal === 'function') {
        if (!this.mobileCartSheet.open) this.mobileCartSheet.showModal();
      } else {
        this.mobileCartSheet.setAttribute('open', '');
      }
      document.documentElement.classList.add('minaco-product-mobile-cart-open');
    }

    closeMobileCartSheet() {
      if (!this.mobileCartSheet) return;
      if (typeof this.mobileCartSheet.close === 'function' && this.mobileCartSheet.open) {
        this.mobileCartSheet.close();
      } else {
        this.mobileCartSheet.removeAttribute('open');
      }
      document.documentElement.classList.remove('minaco-product-mobile-cart-open');
    }

    async openCartFromMobileSheet() {
      this.closeMobileCartSheet();
      const drawer = window.MinacoCartDrawerController;
      if (drawer?.refresh) await drawer.refresh();
      if (drawer?.open) {
        drawer.open(this.addButton);
      } else {
        document.dispatchEvent(new CustomEvent('minaco:cart:open', { detail: { trigger: this.addButton } }));
      }
    }

    clearAjaxFormMessage(form) {
      form.querySelector('[data-minaco-ajax-form-message]')?.remove();
    }

    showAjaxFormMessage(form, message) {
      let alert = form.querySelector('[data-minaco-ajax-form-message]');
      if (!alert) {
        alert = document.createElement('div');
        alert.className = 'minaco-product__form-message';
        alert.dataset.minacoAjaxFormMessage = '';
        alert.setAttribute('role', 'alert');
        form.appendChild(alert);
      }
      alert.textContent = message;
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-position]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((fieldset) => fieldset.querySelector('[data-option-value]:checked')?.value || '');
    }

    matchingVariant(options) {
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );
    }

    syncVariant(updateUrl) {
      if (!this.variantSelect || !this.variants.length) return;
      const selectedOptions = this.selectedOptions();
      const variant = selectedOptions.length
        ? this.matchingVariant(selectedOptions)
        : this.variants.find((item) => String(item.id) === this.variantSelect.value);

      this.updateOptionAvailability(selectedOptions);

      if (!variant) {
        this.addButton.disabled = true;
        if (this.addLabel) this.addLabel.textContent = this.addButton.dataset.soldLabel;
        return;
      }

      this.variantSelect.value = String(variant.id);
      const selectedOption = Array.from(this.variantSelect.options).find(
        (option) => option.value === String(variant.id)
      );
      const available = Boolean(variant.available);

      if (selectedOption?.dataset.price && this.price) {
        this.price.textContent = selectedOption.dataset.price;
      }
      this.addButton.disabled = !available;
      if (this.addLabel) {
        this.addLabel.textContent = available
          ? this.addButton.dataset.addLabel
          : this.addButton.dataset.soldLabel;
      }

      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      this.syncColorGallery();
    }

    selectedColorGalleryKey() {
      return this.querySelector('.minaco-product__option--color [data-option-value]:checked')?.dataset.colorGalleryKey || '';
    }

    syncColorGallery() {
      if (!this.gallery) return;

      const requestedKey = this.selectedColorGalleryKey();
      const allSlides = Array.from(
        this.gallery.querySelectorAll('[data-minaco-product-slide]:not([data-minaco-carousel-clone])')
      );
      const matchingSlides = requestedKey
        ? allSlides.filter((slide) => slide.dataset.galleryKey === requestedKey)
        : [];
      const activeKey = matchingSlides.length ? requestedKey : '__fallback';

      this.removeCarouselClones();
      allSlides.forEach((slide) => {
        slide.hidden = slide.dataset.galleryKey !== activeKey;
        slide.setAttribute('aria-hidden', String(slide.hidden));
        slide.querySelectorAll('video').forEach((video) => {
          if (slide.hidden) {
            video.pause();
          } else {
            video.play().catch(() => {});
          }
        });
      });

      this.lightboxSlides.forEach((slide) => {
        const inactive = slide.dataset.galleryKey !== activeKey;
        slide.hidden = inactive;
        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
        if (inactive) slide.querySelectorAll('video').forEach((video) => video.pause());
      });

      this.currentGalleryKey = activeKey;
      this.currentLightboxIndex = 0;
      const activeSlides = allSlides.filter((slide) => !slide.hidden);
      this.realSlideCount = activeSlides.length;
      if (this.progress) this.progress.hidden = activeSlides.length <= 1;
      if (this.progressThumb) {
        this.progressThumb.style.setProperty('--mp-progress-count', String(Math.max(activeSlides.length, 1)));
        this.progressThumb.style.setProperty('--mp-progress-index', '0');
      }

      if (this.gallery) this.gallery.scrollLeft = 0;
      this.syncCarousel();
    }

    updateOptionAvailability(selectedOptions) {
      this.querySelectorAll('[data-option-position]').forEach((fieldset) => {
        const position = Number(fieldset.dataset.optionPosition) - 1;
        fieldset.querySelectorAll('[data-option-value]').forEach((input) => {
          const candidate = selectedOptions.slice();
          candidate[position] = input.value;
          const possible = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every((value, index) => !candidate[index] || value === candidate[index])
          );
          input.nextElementSibling?.classList.toggle('is-unavailable', !possible);
          input.nextElementSibling?.setAttribute('aria-disabled', String(!possible));
        });
      });
    }

    handleClick(event) {
      const inlineVideo = event.target.closest('.minaco-product__media-video');
      if (inlineVideo && this.contains(inlineVideo)) {
        if (inlineVideo.paused) {
          inlineVideo.play().catch(() => {});
        } else {
          inlineVideo.pause();
        }
        return;
      }

      const accordionTrigger = event.target.closest('[data-minaco-accordion-trigger]');
      if (accordionTrigger && this.contains(accordionTrigger)) {
        const panel = this.querySelector('#' + accordionTrigger.getAttribute('aria-controls'));
        const willOpen = accordionTrigger.getAttribute('aria-expanded') !== 'true';
        if (willOpen) {
          this.querySelectorAll('[data-minaco-accordion-trigger]').forEach((otherTrigger) => {
            if (otherTrigger === accordionTrigger) return;
            const otherPanel = this.querySelector('#' + otherTrigger.getAttribute('aria-controls'));
            otherTrigger.setAttribute('aria-expanded', 'false');
            otherPanel?.setAttribute('data-open', 'false');
            otherPanel?.setAttribute('aria-hidden', 'true');
          });
        }
        accordionTrigger.setAttribute('aria-expanded', String(willOpen));
        panel?.setAttribute('data-open', String(willOpen));
        panel?.setAttribute('aria-hidden', String(!willOpen));
        return;
      }

      const openButton = event.target.closest('[data-minaco-lightbox-open]');
      if (openButton && this.contains(openButton)) {
        if (this.suppressOpen) {
          event.preventDefault();
          return;
        }
        this.openLightbox(Number(openButton.dataset.minacoLightboxOpen), openButton.dataset.minacoLightboxGroup || this.currentGalleryKey);
        return;
      }

      if (event.target.closest('[data-minaco-lightbox-close]')) {
        this.closeLightbox();
        return;
      }

      if (event.target.closest('[data-minaco-lightbox-previous]')) {
        this.showLightboxSlide(this.currentLightboxIndex - 1);
        return;
      }

      if (event.target.closest('[data-minaco-lightbox-next]')) {
        this.showLightboxSlide(this.currentLightboxIndex + 1);
        return;
      }

      const zoomImage = event.target.closest('img.minaco-product__lightbox-media');
      if (zoomImage && this.lightbox?.contains(zoomImage)) {
        this.toggleLightboxZoom(zoomImage, event);
        return;
      }

      if (event.target === this.lightbox) this.closeLightbox();
    }

    handleKeydown(event) {
      if (!this.lightbox?.open) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showLightboxSlide(this.currentLightboxIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showLightboxSlide(this.currentLightboxIndex + 1);
      }
    }

    activeLightboxSlides() {
      const key = this.currentGalleryKey || '__fallback';
      return this.lightboxSlides.filter((slide) => !slide.hidden && slide.dataset.galleryKey === key);
    }

    openLightbox(index, galleryKey) {
      if (!this.lightbox || !this.lightboxSlides.length) return;
      if (galleryKey) this.currentGalleryKey = galleryKey;
      const activeSlides = this.activeLightboxSlides();
      if (!activeSlides.length) return;
      this.showLightboxSlide(index);
      if (typeof this.lightbox.showModal === 'function') {
        this.lightbox.showModal();
      } else {
        this.lightbox.setAttribute('open', '');
      }
      this.lightbox.querySelector('[data-minaco-lightbox-close]')?.focus();
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.resetLightboxZoom();
      this.pauseInactiveMedia(null);
      if (typeof this.lightbox.close === 'function') {
        this.lightbox.close();
      } else {
        this.lightbox.removeAttribute('open');
      }
    }

    showLightboxSlide(index) {
      const activeSlides = this.activeLightboxSlides();
      const total = activeSlides.length;
      if (!total) return;
      this.resetLightboxZoom();
      this.currentLightboxIndex = (index + total) % total;

      this.lightboxSlides.forEach((slide) => {
        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
      });

      const activeSlide = activeSlides[this.currentLightboxIndex];
      activeSlide.classList.add('is-active');
      activeSlide.setAttribute('aria-hidden', 'false');
      this.pauseInactiveMedia(activeSlide);

      const hideArrows = total <= 1;
      if (this.lightboxPrevious) this.lightboxPrevious.hidden = hideArrows;
      if (this.lightboxNext) this.lightboxNext.hidden = hideArrows;
    }

    toggleLightboxZoom(image, event) {
      const slide = image.closest('[data-minaco-lightbox-slide]');
      if (!slide) return;
      const willZoom = !slide.classList.contains('is-zoomed');
      if (willZoom) {
        const bounds = image.getBoundingClientRect();
        const x = bounds.width ? ((event.clientX - bounds.left) / bounds.width) * 100 : 50;
        const y = bounds.height ? ((event.clientY - bounds.top) / bounds.height) * 100 : 50;
        slide.style.setProperty('--mp-zoom-x', `${Math.max(0, Math.min(100, x))}%`);
        slide.style.setProperty('--mp-zoom-y', `${Math.max(0, Math.min(100, y))}%`);
      }
      slide.classList.toggle('is-zoomed', willZoom);
      image.setAttribute('aria-label', willZoom ? 'Zoom out' : 'Zoom in');
    }

    resetLightboxZoom() {
      this.lightboxSlides.forEach((slide) => {
        slide.classList.remove('is-zoomed');
        slide.style.removeProperty('--mp-zoom-x');
        slide.style.removeProperty('--mp-zoom-y');
        slide.querySelector('img.minaco-product__lightbox-media')?.setAttribute('aria-label', 'Zoom in');
      });
    }

    pauseInactiveMedia(activeSlide) {
      this.lightboxSlides.forEach((slide) => {
        if (slide === activeSlide) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      activeSlide?.querySelectorAll('video').forEach((video) => video.play().catch(() => {}));
    }

    handlePointerDown(event) {
      this.pointerStartX = event.clientX;
    }

    handlePointerUp(event) {
      if (typeof this.pointerStartX !== 'number') return;
      if (Math.abs(event.clientX - this.pointerStartX) > 8) {
        this.suppressOpen = true;
        window.setTimeout(() => {
          this.suppressOpen = false;
        }, 120);
      }
      this.pointerStartX = null;
    }

    handleBreakpointChange() {
      this.syncCarousel();
    }

    handleResize() {
      this.syncHeaderOffset();
      if (!this.carouselReady || !this.mobileQuery.matches) return;
      window.clearTimeout(this.carouselTimer);
      this.carouselTimer = window.setTimeout(() => this.normalizeCarousel(), 80);
    }

    syncCarousel() {
      if (!this.gallery) return;
      if (!this.mobileQuery.matches) {
        this.removeCarouselClones();
        const visibleSlides = Array.from(
          this.gallery.querySelectorAll('[data-minaco-product-slide]:not([data-minaco-carousel-clone])')
        ).filter((slide) => !slide.hidden);
        this.realSlideCount = visibleSlides.length;
        this.updateCarouselProgress(0);
        return;
      }

      const realSlides = Array.from(
        this.gallery.querySelectorAll('[data-minaco-product-slide]:not([data-minaco-carousel-clone])')
      ).filter((slide) => !slide.hidden);
      this.realSlideCount = realSlides.length;
      if (realSlides.length <= 1 || this.carouselReady) return;

      const firstClone = realSlides[0].cloneNode(true);
      const lastClone = realSlides[realSlides.length - 1].cloneNode(true);
      this.prepareClone(firstClone);
      this.prepareClone(lastClone);
      this.gallery.insertBefore(lastClone, realSlides[0]);
      this.gallery.appendChild(firstClone);
      this.carouselReady = true;
      this.realSlideCount = realSlides.length;
      requestAnimationFrame(() => this.jumpCarousel(1));
    }

    syncHeaderOffset() {
      const header = document.querySelector('[data-minaco-header]');
      if (!header) return;
      const height = Math.ceil(header.getBoundingClientRect().height);
      if (height > 0) this.style.setProperty('--mp-header-offset-live', `${height}px`);
    }

    syncAccordions() {
      let openFound = false;
      this.querySelectorAll('[data-minaco-accordion-trigger]').forEach((trigger) => {
        const panel = this.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
        const requestedOpen = trigger.getAttribute('aria-expanded') === 'true';
        const isOpen = requestedOpen && !openFound;
        if (isOpen) openFound = true;
        trigger.setAttribute('aria-expanded', String(isOpen));
        panel?.setAttribute('data-open', String(isOpen));
        panel?.setAttribute('aria-hidden', String(!isOpen));
      });
    }

    prepareClone(clone) {
      clone.dataset.minacoCarouselClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('button, a, input, select, textarea, [tabindex]').forEach((item) => {
        item.setAttribute('tabindex', '-1');
      });
    }

    removeCarouselClones() {
      this.gallery?.querySelectorAll('[data-minaco-carousel-clone]').forEach((clone) => clone.remove());
      if (this.gallery) this.gallery.scrollLeft = 0;
      this.carouselReady = false;
    }

    jumpCarousel(position) {
      if (!this.gallery) return;
      const behavior = this.gallery.style.scrollBehavior;
      this.gallery.style.scrollBehavior = 'auto';
      this.gallery.scrollLeft = this.gallery.clientWidth * position;
      this.gallery.style.scrollBehavior = behavior;
      this.updateCarouselProgress(position - 1);
    }

    normalizeCarousel() {
      if (!this.carouselReady || !this.gallery?.clientWidth) return;
      const position = this.gallery.scrollLeft / this.gallery.clientWidth;
      if (position < 0.5) {
        this.jumpCarousel(this.realSlideCount);
      } else if (position > this.realSlideCount + 0.5) {
        this.jumpCarousel(1);
      } else {
        this.updateCarouselProgress(Math.round(position) - 1);
      }
    }

    updateCarouselProgress(index) {
      if (!this.progressThumb || !this.realSlideCount) return;
      const normalizedIndex = ((index % this.realSlideCount) + this.realSlideCount) % this.realSlideCount;
      this.progressThumb.style.setProperty('--mp-progress-index', String(normalizedIndex));
    }

    handleCarouselScroll() {
      if (!this.carouselReady || !this.mobileQuery.matches) return;
      const position = this.gallery.clientWidth ? this.gallery.scrollLeft / this.gallery.clientWidth : 1;
      this.updateCarouselProgress(Math.round(position) - 1);
      window.clearTimeout(this.carouselTimer);
      this.carouselTimer = window.setTimeout(() => this.normalizeCarousel(), 90);
    }
  }

  customElements.define('minaco-product', MinacoProduct);
})();
